import { Alert, Button, Group, Tabs } from '@mantine/core';
import {
  IconBooks,
  IconCircleCheck,
  IconListDetails,
  IconSend,
  IconSitemap,
  IconTool,
} from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import { AddEntryDrawer, type AddTarget } from './AddDrawers.js';
import { ApplicationsTab } from './Applications.js';
import { ApplyWizard } from './ApplyWizard.js';
import { ImportExportTab } from './ImportExport.js';
import {
  ComponentLibraryTab,
  ItemDrawer,
  JobDrawer,
  JobLibraryTab,
  useLibraryErrorText,
} from './LibraryTables.js';
import { readApplyVessel } from './library-setup.js';
import classes from './PmsLibrary.module.css';
import type { PmsLibrarySource } from './source.js';
import type {
  CreatedEntry,
  LibraryApplication,
  LibraryItem,
  LibraryJob,
  LibraryVessel,
  PmsLibrary,
} from './types.js';
import { hasInterval } from './view.js';

export interface PmsLibraryScreenProps {
  source: PmsLibrarySource;
  /** Bugün `YYYY-MM-DD` (UTC; sihirbazda başlangıç günü gelecekte olamaz). */
  today: string;
}

type Tab = 'components' | 'jobs' | 'importExport' | 'applications';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | {
      kind: 'ready';
      library: PmsLibrary;
      vessels: LibraryVessel[];
      applications: LibraryApplication[];
    };

/**
 * Maintenance (PMS) ▸ PMS Library (M15f; ADR 0063 §10): şirketin sistem, ekipman kalemi ve iş
 * kütüphanesi. Ortağın menüsündeki Component Library, Job Library ve Import / Export sekmeler;
 * "Apply to Vessel" sihirbazı ve gemi uygulamalarının durumu. Web ofiste kullanılır; gemide talep
 * Equipment List'te görünür.
 */
export function PmsLibraryScreen({ source, today }: PmsLibraryScreenProps) {
  const { t } = useTranslation();
  const errorText = useLibraryErrorText();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [tab, setTab] = useState<Tab>('components');
  const [item, setItem] = useState<LibraryItem | null>(null);
  const [job, setJob] = useState<LibraryJob | null>(null);
  const [adding, setAdding] = useState<AddTarget | null>(null);
  const [wizard, setWizard] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  // Equipment List'in boş ağacından gelen "Set up from library" (04.11 notları P1): sihirbaz o
  // gemi seçili bir kez açılır; kütüphane boşsa ya da uygulama izni yoksa açılmaz
  const shell = useShell();
  const applyVessel = readApplyVessel(shell?.search ?? '');
  const autoOpened = useRef(false);

  const load = useCallback(async () => {
    try {
      const [library, vessels, applications] = await Promise.all([
        source.load(),
        source.vessels(),
        source.applications(),
      ]);
      setState({ kind: 'ready', library, vessels, applications });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (autoOpened.current || state.kind !== 'ready' || !applyVessel) return;
    autoOpened.current = true;
    if (
      state.library.items.length > 0 &&
      state.library.can?.apply !== false &&
      state.vessels.some((vessel) => vessel.id === applyVessel)
    ) {
      setWizard(true);
    }
  }, [state, applyVessel]);

  const header = (
    <PageHeader
      title={t('pmsLibrary.title')}
      description={t('pmsLibrary.description')}
      actions={
        state.kind === 'ready' &&
        state.library.items.length > 0 &&
        state.library.can?.apply !== false ? (
          <Button leftSection={<IconSend size={16} />} onClick={() => setWizard(true)}>
            {t('pmsLibrary.actions.apply')}
          </Button>
        ) : undefined
      }
    />
  );

  if (state.kind === 'loading') {
    return (
      <div className={classes.screen}>
        {header}
        <TableSkeleton columns={6} label={t('pmsLibrary.title')} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={classes.screen}>
        {header}
        <Panel>
          <LoadError
            code={state.code}
            title={t('pmsLibrary.error')}
            onRetry={() => {
              setState({ kind: 'loading' });
              void load();
            }}
          />
        </Panel>
      </div>
    );
  }

  const { library, vessels, applications } = state;
  const canEdit = library.can?.edit ?? false;
  const canImport = library.can?.import ?? false;
  const canAdd = library.can?.add ?? false;
  const approved = library.jobs.filter(
    (j) => j.status === 'approved' && hasInterval(j.interval),
  ).length;
  const done = async (message: string) => {
    setNotice(message);
    await load();
  };
  const added = async (kind: AddTarget['kind'], entry: CreatedEntry) => {
    await done(t(`pmsLibrary.add.${kind}.done`, { code: entry.code }));
  };
  const addJob = (target: LibraryItem | null) => {
    setItem(null);
    setAdding({ kind: 'job', itemId: target?.id ?? null });
  };
  const loadTemplate = async () => {
    setTemplateBusy(true);
    setTemplateError(null);
    try {
      await source.importTemplate(false);
      await load();
    } catch (error) {
      setTemplateError(errorText(error));
    } finally {
      setTemplateBusy(false);
    }
  };

  return (
    <div className={classes.screen}>
      {header}
      {notice ? (
        <Alert
          color="green"
          withCloseButton
          closeButtonLabel={t('pmsLibrary.actions.close')}
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      ) : null}
      <div className={classes.stats}>
        <StatTile
          icon={<IconSitemap size={20} />}
          label={t('pmsLibrary.stats.systems')}
          value={library.systems.length}
        />
        <StatTile
          icon={<IconTool size={20} />}
          label={t('pmsLibrary.stats.items')}
          value={library.items.length}
        />
        <StatTile
          icon={<IconListDetails size={20} />}
          label={t('pmsLibrary.stats.jobs')}
          value={library.jobs.length}
        />
        <StatTile
          icon={<IconCircleCheck size={20} />}
          label={t('pmsLibrary.stats.approved')}
          value={approved}
          tone={approved > 0 ? 'success' : 'default'}
        />
      </div>
      {library.items.length === 0 && tab !== 'importExport' ? (
        <Panel>
          <EmptyState
            icon={<IconBooks size={28} />}
            title={t('pmsLibrary.empty.title')}
            description={t('pmsLibrary.empty.description')}
            action={
              <Group justify="center">
                {canImport ? (
                  <Button loading={templateBusy} onClick={() => void loadTemplate()}>
                    {t('pmsLibrary.actions.loadTemplate')}
                  </Button>
                ) : null}
                <Button variant="default" onClick={() => setTab('importExport')}>
                  {t('pmsLibrary.tabs.importExport')}
                </Button>
              </Group>
            }
          />
          {templateError ? <Alert color="red">{templateError}</Alert> : null}
        </Panel>
      ) : (
        <Tabs
          className={classes.tabs}
          value={tab}
          onChange={(value) => setTab((value as Tab) ?? 'components')}
          keepMounted={false}
        >
          <Tabs.List>
            <Tabs.Tab value="components">{t('pmsLibrary.tabs.components')}</Tabs.Tab>
            <Tabs.Tab value="jobs">{t('pmsLibrary.tabs.jobs')}</Tabs.Tab>
            <Tabs.Tab value="importExport">{t('pmsLibrary.tabs.importExport')}</Tabs.Tab>
            <Tabs.Tab value="applications">{t('pmsLibrary.tabs.applications')}</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="components" className={classes.tabPanel}>
            <ComponentLibraryTab
              library={library}
              canEdit={canEdit}
              canAdd={canAdd}
              onEdit={setItem}
              onAddSystem={() => setAdding({ kind: 'system' })}
              onAddItem={(systemId) => setAdding({ kind: 'item', systemId })}
              onAddJob={addJob}
            />
          </Tabs.Panel>
          <Tabs.Panel value="jobs" className={classes.tabPanel}>
            <JobLibraryTab
              library={library}
              canEdit={canEdit}
              canAdd={canAdd}
              onEdit={setJob}
              onAddJob={() => addJob(null)}
            />
          </Tabs.Panel>
          <Tabs.Panel value="importExport" className={classes.tabPanel}>
            <ImportExportTab source={source} canImport={canImport} onImported={load} />
          </Tabs.Panel>
          <Tabs.Panel value="applications" className={classes.tabPanel}>
            <ApplicationsTab applications={applications} vessels={vessels} />
          </Tabs.Panel>
        </Tabs>
      )}
      <ItemDrawer
        item={item}
        source={source}
        canAdd={canAdd}
        onClose={() => setItem(null)}
        onSaved={load}
        onAddJob={addJob}
      />
      <AddEntryDrawer
        target={adding}
        library={library}
        source={source}
        onClose={() => setAdding(null)}
        onAdded={added}
      />
      <JobDrawer job={job} source={source} onClose={() => setJob(null)} onSaved={load} />
      <ApplyWizard
        opened={wizard}
        library={library}
        vessels={vessels}
        source={source}
        today={today}
        {...(applyVessel ? { initialVesselId: applyVessel } : {})}
        onClose={() => setWizard(false)}
        onDone={done}
      />
    </div>
  );
}
